


























import { computed, defineComponent, onMounted, onUnmounted, ref, set, toRef, watch } from "@vue/composition-api";
import { Route } from "vue-router";
import { PaymentPackage, PaymentPackageEnum } from "@scrile/api-provider/dist/api/PaymentPackageProvider";
import { SubscriptionLevel } from "@scrile/api-provider/dist/api/SubscriptionLevelProvider";
import { Subscription } from "@scrile/api-provider/dist/api/SubscriptionProvider";
import { User } from "@scrile/api-provider/dist/api/UserProvider";
import {
  ContentFeed,
  ContentFeedSearchResult,
  ContentFeedTypeEnum,
  InputContentFeedSearch,
} from "@scrile/api-provider/dist/projects/scrileConnect/ContentFeedProvider";
import { UserPublicData } from "@scrile/api-provider/dist/projects/scrileConnect/UserPublicDataProvider";
import { checkAuthorities, checkRole } from "@scrile/tools/dist/lib/SecurityHelpers";
import useAppointments from "@/hooks/useAppointments";
import useAuthUser from "@/hooks/useAuthUser";
import useContentFeed from "@/hooks/useContentFeed";
import useFollowing from "@/hooks/useFollowing";
import useNSFWUserSettings from "@/hooks/useNSFWUserSettings";
import providers from "@/lib/api";
import { getPurchasedContentFeeds } from "@/lib/contentFeedHelper";
import emitter from "@/lib/emitter";
import { userPublicDataFieldsWithStoryInfo } from "@/lib/fieldsHelper";
import { getProfileUrl } from "@/lib/urlHelpers";
import { resolveUserIdByRoute } from "@/lib/userHelpers";
import { feedFields } from "@/helpers";
import config from "@/config";
import { ViewStoriesSettings } from "@/types";
import { EVENTS, SUBSCRIPTION_LEVEL_FRAGMENT } from "@/const";
import PageProfileView from "./components/PageProfileView";

export default defineComponent({
  name: "PageProfile",
  components: { PageProfileView },
  props: {
    userId: { type: String, required: true },
    feedId: { type: String },
    storyId: { type: String },
  },
  setup(props, { root }) {
    const { authorities, authUser, allowAccess } = useAuthUser();
    const { contentFeedSearch } = useContentFeed();
    const { isBookingAvailable, checkBookingAvailable } = useAppointments();
    const { showNotSafeForWork } = useNSFWUserSettings();
    const followingIds = computed(() => [props.userId]);
    const { toggle, followingIdsList, checkAllFollowingsById } = useFollowing(followingIds, {
      globalCapture: true,
      watchMode: true,
    });
    const loading = ref(true);
    const user = ref<UserPublicData | null>(null);
    const questionaryInfo = ref<Record<string, any>>({});
    const contentFeedSearchResult = ref<ContentFeedSearchResult | null>(null);
    const subscriptionLevels = ref<SubscriptionLevel[]>([]);
    const activeSubscriptions = ref<Subscription[]>([]);
    const paymentPackages = ref<PaymentPackage[]>([]);
    const scroll = ref(false);
    const isFollowing = computed(() => followingIdsList.value.includes(props.userId));
    const initialShowNotSafeForWork = ref(showNotSafeForWork.value);

    const allowPinPosts = computed(() => config.featurePinPost);

    const contentFeedSearchSort = computed(() =>
      allowPinPosts.value ? "PIN_TIME;PUBLISH_TIME,DESC" : "PUBLISH_TIME,DESC"
    );

    const isAuthor = computed(() => {
      return !!authUser.value && authUser.value.id === props.userId;
    });
    const canUpdateContentSet = computed(() => {
      return checkAuthorities(authorities.value, isAuthor.value ? ["createMyContentSet"] : ["createAnyContentSet"]);
    });
    const checkAppointments = async (userId: string) => {
      if (isAuthor.value || !authUser.value) {
        isBookingAvailable.value = false;
        return;
      }
      await checkBookingAvailable(userId);
    };
    const onFeedClick = (feedId: string) => {
      if (user.value) root.$router.push(getProfileUrl(user.value, { feedId }));
    };
    const onFollowUpdate = () => {
      toggle(props.userId);
    };
    const onContentFeedUpdate = (contentFeed: ContentFeed) => {
      if (!contentFeedSearchResult.value) return;

      const result = contentFeedSearchResult.value.result.map(item =>
        item.id === contentFeed.id ? contentFeed : item
      );

      const itemsWithPinTime = result.filter(item => item.pinTime !== null);
      const itemsWithoutPinTime = result.filter(item => item.pinTime === null);

      if (itemsWithPinTime.length && itemsWithPinTime.length > config.maxAllowedPins) {
        const oldestItemWithPinTime = itemsWithPinTime.shift();
        oldestItemWithPinTime && itemsWithoutPinTime.push({ ...oldestItemWithPinTime, pinTime: null });
      }

      const sortedItemsWithPinTime = [...itemsWithPinTime].sort(
        (a, b) => new Date(a.pinTime ?? 0).getTime() - new Date(b.pinTime ?? 0).getTime()
      );
      const sortedItemsWithoutPinTime = [...itemsWithoutPinTime].sort(
        (a, b) =>
          new Date(b.publishTime ?? b.scheduleTime ?? b.createTime).getTime() -
          new Date(a.publishTime ?? b.scheduleTime ?? b.createTime).getTime()
      );

      set(contentFeedSearchResult.value, "result", [...sortedItemsWithPinTime, ...sortedItemsWithoutPinTime]);
    };
    const onFeedDelete = (feedId: string) => {
      if (!contentFeedSearchResult.value) return;
      set(
        contentFeedSearchResult.value,
        "result",
        contentFeedSearchResult.value.result.filter(item => item.id !== feedId)
      );
    };

    const getContentFeed = async (
      userId: string,
      data?: Partial<InputContentFeedSearch>,
      replace: boolean = true,
      feedIdToFind?: string
    ) => {
      try {
        scroll.value = false;
        let response = await contentFeedSearch(
          {
            userId: userId,
            sort: contentFeedSearchSort.value,
            processingStatus: canUpdateContentSet.value ? undefined : "OK",
            published: true,
            includeScheduled: canUpdateContentSet.value,
            types: [ContentFeedTypeEnum.MEDIA, ContentFeedTypeEnum.EVENT],
            ...(!showNotSafeForWork.value ? { safeForWork: true } : {}),
            ...data,
          },
          feedIdToFind,
          feedFields()
        );
        if (replace) {
          contentFeedSearchResult.value = response;
        } else {
          contentFeedSearchResult.value = {
            ...response,
            result: [
              ...(contentFeedSearchResult.value ? contentFeedSearchResult.value.result : []),
              ...response.result,
            ],
          };
        }
        return contentFeedSearchResult.value;
      } finally {
        scroll.value = contentFeedSearchResult.value?.hasNextPage ?? false;
      }
    };
    const onNext = async (cb?: Function) => {
      try {
        if (contentFeedSearchResult.value && contentFeedSearchResult.value.hasNextPage) {
          await getContentFeed(props.userId, { page: contentFeedSearchResult.value.page + 1 }, false);
        }
      } finally {
        cb && cb();
      }
    };

    const checkActiveSubscriptions = async () => {
      if (authUser.value && checkRole(user.value?.role ?? "", "creator")) {
        activeSubscriptions.value = (
          await providers.SubscriptionProvider.search({
            data: {
              consumerIds: [authUser.value.id],
              providerId: props.userId,
              expired: false,
            },
          })
        ).result;
      }
    };

    const getSubscriptionLevel = async () => {
      if (user.value?.verified) {
        subscriptionLevels.value = await providers.SubscriptionLevelProvider.find(
          { userIds: [user.value.id] },
          SUBSCRIPTION_LEVEL_FRAGMENT
        );
      }
    };

    const getUser = async (userId: string = props.userId) => {
      const response = await providers.UserPublicDataProvider.search(
        { data: { id: [userId] } },
        userPublicDataFieldsWithStoryInfo()
      );
      user.value = response.result[0];
    };

    const getProfileData = async (userId: string, oldUserId?: string) => {
      try {
        loading.value = true;
        user.value = null;
        questionaryInfo.value = {};
        contentFeedSearchResult.value = null;
        activeSubscriptions.value = [];
        subscriptionLevels.value = [];
        const importantPromises: Promise<any>[] = [
          getUser(userId),
          providers.QuestionaryInfoProvider.find({ id: userId }).then(userQuestionaryInfo => {
            questionaryInfo.value = userQuestionaryInfo.fields;
          }),
          getContentFeed(userId, {}, true, props.feedId || undefined),
          providers.PaymentPackageProvider.findAll({ type: [PaymentPackageEnum.FILESET] }).then(
            r => (paymentPackages.value = r)
          ),
        ];
        if (authUser.value && !isAuthor.value) {
          importantPromises.push(checkAllFollowingsById([props.userId]));
        }
        await Promise.all(importantPromises).then(() => (loading.value = false));
        await Promise.all([getSubscriptionLevel(), checkActiveSubscriptions(), checkAppointments(userId)]);
      } finally {
        loading.value = false;
        if (oldUserId && oldUserId !== userId) {
          window.scrollTo({ top: 0 });
        }
      }
    };

    const updateFeed = async (payload: { userId: string; feedId?: string }) => {
      if (!contentFeedSearchResult.value) {
        return getContentFeed(props.userId, {}, true);
      }
      set(
        contentFeedSearchResult.value,
        "result",
        await getPurchasedContentFeeds(
          { authorId: payload.userId, purchasedFeedId: payload.feedId },
          contentFeedSearchResult.value,
          {
            includeScheduled: canUpdateContentSet.value,
            types: [ContentFeedTypeEnum.MEDIA, ContentFeedTypeEnum.EVENT],
          },
          feedFields()
        )
      );
    };
    const updateDataForLogin = (newAuthUser: User | null, oldAuthUser: User | null) => {
      if (!oldAuthUser && newAuthUser && newAuthUser.accountInfo.lastLoginTime && allowAccess.value) {
        const resultData = contentFeedSearchResult.value?.result ?? [];
        const userId = resultData.find(() => true)?.user.id;
        if (userId) {
          updateFeed({ userId });
        }
      }
    };

    const showStories = () => {
      if (!config.featureStories) return;
      if (props.storyId && user.value)
        if (checkRole(user?.value.role ?? "", "creator")) {
          emitter.emit<ViewStoriesSettings>(EVENTS.STORIES_SHOW, {
            storyPublishers: [{ userId: props.userId, user: user.value }],
            currentStoryId: props.storyId,
            back: getProfileUrl(user.value),
            routeSupport: true,
          });
        } else {
          if (user.value) root.$router.replace(getProfileUrl(user.value));
        }
    };

    const onSensitiveContent = (value: boolean) => {
      if (initialShowNotSafeForWork.value !== value && props.userId) {
        getContentFeed(props.userId, {}, true, props.feedId || undefined);
      }
      initialShowNotSafeForWork.value = value;
    };

    watch(toRef(props, "userId"), (newUserId, oldUserId) => {
      return getProfileData(props.userId, oldUserId);
    });

    watch(authUser, (newAuthUser, oldAuthUser) => {
      updateDataForLogin(newAuthUser, oldAuthUser);
    });

    onMounted(() => {
      getProfileData(props.userId).then(() => showStories());

      emitter.on<any>(EVENTS.CONTENT_FEED_UPDATED, updateFeed);
      emitter.on<any>(EVENTS.CONTENT_FEED_PURCHASED, updateFeed);
      emitter.on<any>(EVENTS.CONTENT_FEED_PURCHASED, checkActiveSubscriptions);
    });

    onUnmounted(() => {
      emitter.off<any>(EVENTS.CONTENT_FEED_UPDATED, updateFeed);
      emitter.off<any>(EVENTS.CONTENT_FEED_PURCHASED, updateFeed);
      emitter.off<any>(EVENTS.CONTENT_FEED_PURCHASED, checkActiveSubscriptions);
    });

    return {
      user,
      scroll,
      loading,
      isAuthor,
      isFollowing,
      paymentPackages,
      questionaryInfo,
      subscriptionLevels,
      isBookingAvailable,
      canUpdateContentSet,
      activeSubscriptions,
      contentFeedSearchResult,
      onNext,
      onFeedClick,
      onFeedDelete,
      onFollowUpdate,
      onContentFeedUpdate,
      onSensitiveContent,
      getUser,
    };
  },
  beforeRouteEnter(to: Route, from: Route, next: any) {
    resolveUserIdByRoute(to, from, next);
  },
  beforeRouteUpdate(to: Route, from: Route, next: any) {
    resolveUserIdByRoute(to, from, next);
  },
});
